import AutomationRuleBuilder from 'models/automation/Model.AutomationRuleBuilder';
import {
  ArithmeticOperators,
  AutomationTokenTypes,
  BracketTokens,
  EffectTypes,
  ExpressionTypes,
  LogicalOperators,
  RelationalOperators,
} from 'models/enums/Automation';
import {
  AutomationAssignEffectMetadata,
  AutomationPathIdEffectMetadata,
  AutomationPropertyMetadata,
  AutomationRuleLike,
  AutomationRuleSchema,
  createAssignEffect,
  defaultAutomationExpression,
  getAutomationExpressionTypeDefaultValue,
  isValueExpressionType,
  isValuePropertyExpressionType,
} from './AutomationTypes';

export function getExpressionByReference(rule: Partial<AutomationRuleLike>, referenceId?: string) {
  if (!referenceId) return undefined;
  return rule.expressions?.find((expression) => expression.uid === referenceId);
}

export function getPrimaryExpression(rule: Partial<AutomationRuleLike>) {
  const expressionToken = rule.logicalLayout?.find((token) => token.type === AutomationTokenTypes.Expression);
  const [fallbackExpression] = rule.expressions || [];
  return getExpressionByReference(rule, expressionToken?.referenceId) || fallbackExpression;
}

export function getEffectByPropertyPath(rule: Partial<AutomationRuleLike>, propertyPath: string) {
  return rule.effects?.find((effect) => effect.propertyPath === propertyPath);
}

export function getEffectByMetadata(rule: Partial<AutomationRuleLike>, metadata: AutomationAssignEffectMetadata) {
  return getEffectByPropertyPath(rule, metadata.propertyPath);
}

export function getPropertyMetadata(schema: AutomationRuleSchema, propertyPath?: string | null) {
  if (!propertyPath) return undefined;
  return schema.properties[propertyPath];
}

export function getSupportedExpressionTypes(
  schema: AutomationRuleSchema,
  propertyPath?: string | null
): Model.Enum.Automation.ExpressionTypes[] {
  const propertyExpressionTypes = getPropertyMetadata(schema, propertyPath)?.supportedExpressions;
  if (propertyExpressionTypes) return propertyExpressionTypes.filter((type) => type !== ExpressionTypes.ValueJson);

  return Array.from(
    new Set(
      schema.expressionProperties.flatMap(
        (expressionProperty) => getPropertyMetadata(schema, expressionProperty)?.supportedExpressions || []
      )
    )
  ).filter((type) => type !== ExpressionTypes.ValueJson);
}

export function getExpressionTypeDefaultValue(
  schema: AutomationRuleSchema,
  type: Model.Enum.Automation.ExpressionTypes | undefined,
  metadata?: AutomationPropertyMetadata
) {
  const typedDefaultValue = getAutomationExpressionTypeDefaultValue(type);
  if (typedDefaultValue !== undefined) return typedDefaultValue;

  const metadataDefaultValue = metadata?.getDefaultValue?.(type) ?? metadata?.defaultValue ?? '';
  if (type === ExpressionTypes.Any) return '';
  if (type === ExpressionTypes.Contains) {
    return serializeContainsExpressionValue(metadata, metadataDefaultValue);
  }
  if (type === ExpressionTypes.Range) {
    return serializeRangeValue(parseRangeValue(metadataDefaultValue?.toString?.() ?? ''));
  }
  return metadataDefaultValue?.toString?.() ?? '';
}

export function isRangeExpression(expression?: Partial<Model.Automation.IExpression>) {
  return expression?.type === ExpressionTypes.Range;
}

export function isAnyExpression(expression?: Partial<Model.Automation.IExpression>) {
  return expression?.type === ExpressionTypes.Any;
}

function getContainsValuePath(metadata?: AutomationPropertyMetadata) {
  return metadata?.containsValuePath ?? '.';
}

export function serializeContainsExpressionValue(metadata: AutomationPropertyMetadata | undefined, value: any) {
  return JSON.stringify({
    [getContainsValuePath(metadata)]: value ?? '',
  });
}

export function parseContainsExpressionValue(metadata: AutomationPropertyMetadata | undefined, value?: string) {
  if (!value) return '';

  try {
    return JSON.parse(value)?.[getContainsValuePath(metadata)] ?? '';
  } catch {
    return '';
  }
}

export function getExpressionDisplayValue(
  expression: Model.Automation.IExpression,
  metadata?: AutomationPropertyMetadata
) {
  if (isValuePropertyExpressionType(expression.type)) return '';
  if (isAnyExpression(expression)) return '';

  if (isRangeExpression(expression)) {
    const rangeValue = parseRangeValue(expression.value);
    return `${rangeValue.min} - ${rangeValue.max}`;
  }

  const value =
    expression.type === ExpressionTypes.Contains
      ? parseContainsExpressionValue(metadata, expression.value)
      : expression.value;

  if (metadata?.formatValue) return metadata.formatValue(value);
  return value?.toString?.() ?? '';
}

export function createRule(schema: AutomationRuleSchema, data?: Partial<AutomationRuleLike>): AutomationRuleLike {
  const defaultExpression = createDefaultExpression();
  const builder = new AutomationRuleBuilder({});

  builder.addExpression(defaultExpression);

  return {
    ...data,
    logicalLayout: builder.logicalLayout,
    expressions: builder.expressions,
    effects: schema.defaultEffects ? [...schema.defaultEffects] : [],
  } as AutomationRuleLike;
}

export function createDefaultExpression() {
  return {
    ...defaultAutomationExpression,
    propertyPath: '',
    type: undefined,
    value: '',
  } as Omit<Model.Automation.IExpression, 'uid'>;
}

function hasExpressionValue(value: any): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return !isNaN(value);
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(hasExpressionValue);
  if (typeof value === 'object') return Object.values(value).some(hasExpressionValue);
  return true;
}

function hasContainsExpressionValue(value?: string | null) {
  if (!hasExpressionValue(value)) return false;

  try {
    return hasExpressionValue(JSON.parse(value));
  } catch {
    return hasExpressionValue(value);
  }
}

function hasRangeExpressionValue(value?: string | null) {
  if (!hasExpressionValue(value)) return false;

  try {
    const parsed = JSON.parse(value);
    return parsed?.min !== undefined && parsed?.min !== null && parsed?.max !== undefined && parsed?.max !== null;
  } catch {
    return false;
  }
}

export function isIncompleteExpression(expression?: Partial<Model.Automation.IExpression>) {
  if (!expression?.type) return true;
  if (isExpressionPropertyPathMissing(expression)) return true;

  return isExpressionValueMissing(expression);
}

export function isExpressionPropertyPathMissing(expression?: Partial<Model.Automation.IExpression>) {
  return Boolean(expression?.type && !isValueExpressionType(expression.type) && !expression.propertyPath);
}

export function isExpressionValueMissing(expression?: Partial<Model.Automation.IExpression>) {
  if (!expression?.type) return false;
  if (isAnyExpression(expression)) return false;
  if (isValuePropertyExpressionType(expression.type)) return !hasExpressionValue(expression.value);
  if (isRangeExpression(expression)) return !hasRangeExpressionValue(expression.value);
  if (expression.type === ExpressionTypes.Contains) return !hasContainsExpressionValue(expression.value);

  return !hasExpressionValue(expression.value);
}

export function hasIncompleteAutomationExpressions(rule?: Partial<AutomationRuleLike>) {
  return Boolean(rule?.expressions?.some(isIncompleteExpression));
}

export function isEffectValueMissing(effect?: Partial<Model.Automation.IEffect>) {
  return !hasExpressionValue(effect?.value);
}

export function isAssignFromSourceEffectValueMissing(effect?: Partial<Model.Automation.IEffect>) {
  return !hasExpressionValue(effect?.sourcePropertyPath);
}

function isAssignEffectMetadata(
  metadata: AutomationPathIdEffectMetadata | AutomationAssignEffectMetadata
): metadata is AutomationAssignEffectMetadata {
  return metadata.type === 'assign';
}

export function isAutomationEffectMissing(
  effect: Partial<Model.Automation.IEffect>,
  metadata?: AutomationAssignEffectMetadata
) {
  if (!metadata || !effect.propertyPath) return true;
  if (isEffectPropertyPathMissing(metadata, effect)) return true;
  if (effect.type === EffectTypes.AssignFromSource) return isAssignFromSourceEffectValueMissing(effect);

  return isEffectValueMissing(effect);
}

export function isPathIdEffectValueMissing(
  rule: AutomationRuleLike,
  metadata: AutomationPathIdEffectMetadata
) {
  return getPathIdEffectValues(rule, metadata).length === 0;
}

export function isEffectPropertyPathMissing(
  effectMetadata?: AutomationPathIdEffectMetadata | AutomationAssignEffectMetadata,
  effect?: Partial<Model.Automation.IEffect>
) {
  if (!effectMetadata) return true;
  if (effectMetadata.type === 'pathIdMultiSelect') return !effectMetadata.propertyPathPrefix;

  return !effectMetadata.propertyPath || Boolean(effect && !effect.propertyPath);
}

export function hasIncompleteAutomationEffects(
  rule: Partial<AutomationRuleLike> | undefined,
  schema: AutomationRuleSchema
) {
  if (!rule) return true;

  if (schema.allowMultipleEffects) {
    if (!rule.effects?.length) return true;

    return rule.effects.some((effect) => {
      const effectMetadata = schema.effects.find(
        (metadata): metadata is AutomationAssignEffectMetadata =>
          isAssignEffectMetadata(metadata) && metadata.propertyPath === effect.propertyPath
      );

      return isAutomationEffectMissing(effect, effectMetadata);
    });
  }

  return schema.effects.some((effectMetadata, index) => {
    if (effectMetadata.type === 'pathIdMultiSelect') {
      if (isEffectPropertyPathMissing(effectMetadata)) return true;
      return isPathIdEffectValueMissing(rule as AutomationRuleLike, effectMetadata);
    }

    const effect = getEffectByMetadata(rule, effectMetadata) || rule.effects?.[index];
    if (isEffectPropertyPathMissing(effectMetadata, effect)) return true;
    if (effect?.type === EffectTypes.AssignFromSource) return isAssignFromSourceEffectValueMissing(effect);
    return isEffectValueMissing(effect);
  });
}

function clampLayoutIndex(rule: AutomationRuleLike, layoutIndex: number) {
  return Math.min(Math.max(layoutIndex, 0), rule.logicalLayout?.length || 0);
}

export function insertLayoutToken(rule: AutomationRuleLike, layoutIndex: number, token: Model.Automation.ILayoutToken) {
  const logicalLayout = [...(rule.logicalLayout || [])];
  logicalLayout.splice(clampLayoutIndex(rule, layoutIndex), 0, token);

  return {
    ...rule,
    logicalLayout,
  };
}

export function deleteLayoutToken(rule: AutomationRuleLike, layoutIndex: number) {
  const token = rule.logicalLayout?.[layoutIndex];
  if (!token) return rule;

  if (token.type === AutomationTokenTypes.Expression) {
    return deleteExpression(rule, token.referenceId);
  }

  return {
    ...rule,
    logicalLayout: (rule.logicalLayout || []).filter((_, index) => index !== layoutIndex),
  };
}

export function insertLogicalToken(
  rule: AutomationRuleLike,
  layoutIndex: number,
  referenceId: Utils.ValueOf<typeof LogicalOperators>
) {
  return insertLayoutToken(rule, layoutIndex, {
    type: AutomationTokenTypes.LogicalOperator,
    referenceId,
  });
}

export function insertArithmeticOperatorToken(
  rule: AutomationRuleLike,
  layoutIndex: number,
  referenceId: Utils.ValueOf<typeof ArithmeticOperators>
) {
  return insertLayoutToken(rule, layoutIndex, {
    type: AutomationTokenTypes.ArithmeticOperator,
    referenceId,
  });
}

export function insertRelationalOperatorToken(
  rule: AutomationRuleLike,
  layoutIndex: number,
  referenceId: Utils.ValueOf<typeof RelationalOperators>
) {
  return insertLayoutToken(rule, layoutIndex, {
    type: AutomationTokenTypes.RelationalOperator,
    referenceId,
  });
}

export function insertBracketToken(
  rule: AutomationRuleLike,
  layoutIndex: number,
  referenceId: Utils.ValueOf<typeof BracketTokens>
) {
  return insertLayoutToken(rule, layoutIndex, {
    type: AutomationTokenTypes.Bracket,
    referenceId,
  });
}

export function insertExpressionAtLayoutIndex(
  rule: AutomationRuleLike,
  schema: AutomationRuleSchema,
  layoutIndex: number
) {
  const builder = new AutomationRuleBuilder(rule);

  builder.addExpression(createDefaultExpression());

  const expressionToken = builder.logicalLayout[builder.logicalLayout.length - 1];
  const logicalLayout = builder.logicalLayout.slice(0, -1);
  logicalLayout.splice(clampLayoutIndex(rule, layoutIndex), 0, expressionToken);

  return {
    ...rule,
    logicalLayout,
    expressions: builder.expressions,
  };
}

export function addExpression(rule: AutomationRuleLike, schema: AutomationRuleSchema) {
  return insertExpressionAtLayoutIndex(rule, schema, rule.logicalLayout?.length || 0);
}

export function updateExpression(
  rule: AutomationRuleLike,
  expressionUid: string,
  patch: Partial<Model.Automation.IExpression>
) {
  return {
    ...rule,
    expressions: (rule.expressions || []).map((expression) =>
      expression.uid === expressionUid
        ? ({ ...expression, ...patch, uid: expression.uid } as Model.Automation.IExpression)
        : expression
    ),
  };
}

export function deleteExpression(rule: AutomationRuleLike, expressionUid: string) {
  return {
    ...rule,
    expressions: (rule.expressions || []).filter((expression) => expression.uid !== expressionUid),
    logicalLayout: (rule.logicalLayout || []).filter(
      (token) => token.type !== AutomationTokenTypes.Expression || token.referenceId !== expressionUid
    ),
  };
}

export function createDefaultEffect(schema: AutomationRuleSchema): Model.Automation.IEffect {
  const metadata = schema.effects.find((effect) => effect.type === 'assign');

  if (metadata?.type === 'assign') {
    return createAssignEffect(metadata.propertyPath, metadata.options?.[0]?.id?.toString() ?? '');
  }

  return createAssignEffect(schema.effectProperties[0] || '');
}

export function addEffect(rule: AutomationRuleLike, schema: AutomationRuleSchema) {
  return {
    ...rule,
    effects: [...(rule.effects || []), createDefaultEffect(schema)],
  };
}

export function deleteEffect(rule: AutomationRuleLike, effectIndex: number) {
  const effects = rule.effects || [];

  return {
    ...rule,
    effects: effects.filter((_, index) => index !== effectIndex),
  };
}

export function updateAssignEffect(rule: AutomationRuleLike, propertyPath: string, value: string) {
  const effects = [...(rule.effects || [])];
  const effectIndex = effects.findIndex((effect) => effect.propertyPath === propertyPath);

  if (effectIndex >= 0) {
    effects[effectIndex] = { ...effects[effectIndex], value, type: EffectTypes.Assign };
  } else {
    effects.push(createAssignEffect(propertyPath, value));
  }

  return {
    ...rule,
    effects,
  };
}

export function updateEffect(
  rule: AutomationRuleLike,
  effectIndex: number | undefined,
  patch: Partial<Model.Automation.IEffect>
) {
  const effects = [...(rule.effects || [])];
  const currentEffect = typeof effectIndex === 'number' ? effects[effectIndex] : undefined;
  const effectType = patch.type || currentEffect?.type || EffectTypes.Assign;
  const nextEffect = {
    ...currentEffect,
    ...patch,
    type: effectType,
  } as Model.Automation.IEffect;

  if (typeof effectIndex === 'number') {
    effects[effectIndex] = nextEffect;
  } else {
    const existingIndex = effects.findIndex((effect) => effect.propertyPath === patch.propertyPath);
    if (existingIndex >= 0) {
      effects[existingIndex] = {
        ...effects[existingIndex],
        ...patch,
        type: effectType,
      };
    } else {
      effects.push({
        type: effectType,
        propertyPath: patch.propertyPath || '',
        value: Object.prototype.hasOwnProperty.call(patch, 'value') ? patch.value : '',
        sourcePropertyPath: patch.sourcePropertyPath,
      });
    }
  }

  return {
    ...rule,
    effects,
  };
}

export function getPathIdEffectValues(rule: AutomationRuleLike, metadata: AutomationPathIdEffectMetadata) {
  return (
    rule.effects
      ?.map((effect) => metadata.parseIdFromPropertyPath(effect.propertyPath))
      .filter((id) => id !== null && id !== undefined) || []
  );
}

export function updatePathIdEffects(
  rule: AutomationRuleLike,
  metadata: AutomationPathIdEffectMetadata,
  selectedIds: Array<string | number>
) {
  const otherEffects =
    rule.effects?.filter((effect) => metadata.parseIdFromPropertyPath(effect.propertyPath) === null) || [];
  const selectedEffects = (selectedIds || []).map((id) => ({
    type: EffectTypes.Assign,
    propertyPath: metadata.buildPropertyPath(id),
    value: metadata.value ?? 'true',
  })) as Model.Automation.IEffect[];

  return {
    ...rule,
    effects: [...otherEffects, ...selectedEffects],
  };
}
export function parseRangeValue(value: string): { min: number; max: number } {
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'object' && parsed.min !== undefined && parsed.max !== undefined) {
      return {
        min: parsed?.min !== undefined && parsed?.min !== null ? Number(parsed.min) : 0,
        max: parsed?.max !== undefined && parsed?.max !== null ? Number(parsed.max) : 0,
      };
    }
  } catch {
    return { min: 0, max: 0 };
  }

  return { min: 0, max: 0 };
}

export function serializeRangeValue(value: { min?: number; max?: number }) {
  return JSON.stringify({
    min: value?.min ?? 0,
    max: value?.max ?? 0,
  });
}
